<template>
  <modal title="Transcript" ref="modal">
    <loader :task="task" />
    <div v-if="transcript" class="post_media__transcript">
      <div v-if="hasSegments" class="transcript-segments">
        <div
          v-for="segment in transcript.segments"
          :key="segment.id || segment.startTime"
          class="transcript-segment"
        >
          <span class="transcript-segment__time">{{ formatTime(segment.startTime) }}</span>
          <span
            class="transcript-segment__text"
            v-dompurify-html:postContent="highlightText(segment.text)"
          ></span>
        </div>
      </div>
      <div
        v-else-if="content"
        v-dompurify-html:postContent="content"
        class="transcript-simple"
      ></div>

      <message v-else>No transcript available.</message>
    </div>
  </modal>
</template>

<style lang="scss">
.post_media__transcript {
  overflow-y: auto;
  max-height: 400px;

  .flagged {
    background-color: bisque;
  }

  .transcript-simple {
    line-height: 1.6;
    white-space: pre-wrap;
  }

  .transcript-segments {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .transcript-segment {
    display: flex;
    gap: 16px;
    align-items: flex-start;

    &__time {
      flex-shrink: 0;
      width: 48px;
      font-family: monospace;
      font-size: 0.85em;
      color: #666;
      padding-top: 2px;
    }

    &__text {
      flex: 1;
      line-height: 1.5;
    }
  }
}
</style>

<script lang="ts">
import { defineComponent } from 'vue';
import { replaceAll, StringMatchRule } from '@/util/string';
import { ModalMixin } from '@/mixins';
import Modal from '@/components/modal/Modal.vue';
import Message from '@/components/message/Message.vue';
import Loader from '@/components/loading/AsyncLinearProgress.vue';
import { transcribe } from './post';
import { PostTranscript } from './types';

interface Data {
  task: Promise<PostTranscript> | null,
  transcript: PostTranscript | null,
}

export default defineComponent({
  mixins: [ModalMixin],
  components: {
    Modal,
    Loader,
    Message,
  },
  props: {
    postId: {
      type: Number,
      required: true,
    },
    highlight: {
      type: Array as () => StringMatchRule[],
      default: () => [],
    },
  },
  data(): Data {
    return {
      task: null,
      transcript: null,
    };
  },
  watch: {
    postId: {
      handler() {
        this.load();
      },
      immediate: true,
    },
  },
  methods: {
    load(): void {
      if (!this.postId) {
        return;
      }
      this.task = transcribe(this.postId);
      this.task.then((transcript: PostTranscript) => {
        this.transcript = transcript;
      });
    },
    formatTime(seconds: number): string {
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    },
    highlightText(text: string): string {
      if (!this.highlight.length) return text;
      return replaceAll(
        this.highlight,
        text,
        needle => `<span class="flagged">${needle}</span>`,
      );
    },
  },
  computed: {
    hasSegments(): boolean {
      return !!(this.transcript && this.transcript.segments && this.transcript.segments.length > 0);
    },
    content(): string | null {
      if (this.transcript == null || this.transcript.transcript === null) return null;
      if (!this.highlight.length) return this.transcript.transcript || null;
      return replaceAll(
        this.highlight,
        this.transcript.transcript as string,
        needle => `<span class="flagged">${needle}</span>`,
      );
    },
  },
});
</script>
